import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Check,
  Copy,
  Handshake,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import {
  buildReferralSignupUrl,
  claimReferralPartnerInvite,
  fetchReferralPartnerInvitePreview,
  type ReferralPartnerInvitePreview,
} from '../lib/referralPartner';
import {
  formatCommissionTerms,
  REFERRAL_PARTNER_AGREEMENT_SECTIONS,
  REFERRAL_PARTNER_AGREEMENT_VERSION,
} from '../lib/referralPartnerAgreement';
import { formatReferralDiscountLabel } from '../lib/referralPartnerOffer';
import { getSiteUrl } from '../lib/siteConfig';

type PageState = 'loading' | 'ready' | 'claiming' | 'success' | 'already_claimed' | 'error';

export default function ReferralPartnerInvite() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [preview, setPreview] = useState<ReferralPartnerInvitePreview | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMessage('This invite link is invalid.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await fetchReferralPartnerInvitePreview(token);
        if (cancelled) return;

        if (result.ok) {
          setPreview(result);
          setPageState('ready');
          return;
        }

        if (result.error === 'already_claimed') {
          setPreview(result);
          setReferralCode(result.code ?? null);
          setPageState('already_claimed');
          return;
        }

        setPageState('error');
        setErrorMessage(
          result.error === 'inactive'
            ? 'This referral partner invite is no longer active.'
            : 'This invite link is invalid or has expired.',
        );
      } catch {
        if (!cancelled) {
          setPageState('error');
          setErrorMessage('Unable to load this invite. Please try again later.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleClaim = async () => {
    if (!token || !signerName.trim() || !agreed) return;

    setPageState('claiming');
    setErrorMessage(null);
    try {
      const result = await claimReferralPartnerInvite(token, signerName.trim());
      if (result.ok && result.code) {
        setReferralCode(result.code);
        setPageState('success');
        return;
      }

      if (result.error === 'already_claimed' && result.code) {
        setReferralCode(result.code);
        setPageState('already_claimed');
        return;
      }

      setPageState('ready');
      setErrorMessage('Unable to complete your agreement. Please try again.');
    } catch {
      setPageState('ready');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const signupUrl =
    referralCode != null ? buildReferralSignupUrl(referralCode, getSiteUrl()) : null;

  const copyLink = async () => {
    if (!signupUrl) return;
    try {
      await navigator.clipboard.writeText(signupUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert(signupUrl);
    }
  };

  const commissionLabel =
    preview?.commission_percent != null && preview.commission_type
      ? formatCommissionTerms(preview.commission_percent, preview.commission_type)
      : null;

  const signupDiscountLabel =
    preview?.signup_discount_enabled && preview.signup_discount_percent
      ? formatReferralDiscountLabel(preview.signup_discount_percent)
      : null;

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-violet-600/25 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-blue-600/20 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-lg flex-col px-4 py-8 sm:px-6 sm:py-12">
        <header className="mb-8 text-center">
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-violet-300/90">
            Nexus Book AI
          </p>
          <h1 className="mt-2 text-[28px] font-bold leading-tight tracking-tight sm:text-[32px]">
            Referral Partner Invitation
          </h1>
        </header>

        {pageState === 'loading' ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
          </div>
        ) : null}

        {pageState === 'error' ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-sm">
            <p className="text-[16px] font-semibold text-white">{errorMessage}</p>
            <p className="mt-2 text-[14px] text-white/60">
              Contact Nexus if you believe this is a mistake.
            </p>
          </div>
        ) : null}

        {(pageState === 'ready' || pageState === 'claiming') && preview?.ok ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/20">
                  <Handshake className="h-5 w-5 text-violet-300" />
                </div>
                <div>
                  <p className="text-[15px] text-white/70">Hello{preview.partner_name ? ',' : ''}</p>
                  <p className="text-[22px] font-bold leading-tight">
                    {preview.partner_name || 'Partner'}
                  </p>
                  <p className="mt-2 text-[14px] leading-relaxed text-white/65">
                    Nexus is inviting you to become an official referring partner. Share your unique
                    link with businesses you refer — earn commission when they subscribe.
                  </p>
                </div>
              </div>

              {commissionLabel ? (
                <div className="mt-4 rounded-xl bg-gradient-to-r from-violet-600/30 to-blue-600/30 px-4 py-3 border border-violet-400/20">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-200/80">
                    Your commission
                  </p>
                  <p className="mt-1 text-[16px] font-semibold text-white">{commissionLabel}</p>
                </div>
              ) : null}
              {signupDiscountLabel ? (
                <div className="mt-3 rounded-xl bg-gradient-to-r from-emerald-600/25 to-teal-600/25 px-4 py-3 border border-emerald-400/20">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-200/80">
                    Offer for your referrals
                  </p>
                  <p className="mt-1 text-[16px] font-semibold text-white">{signupDiscountLabel}</p>
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
                <ShieldCheck className="h-4 w-4 text-violet-300" />
                <p className="text-[14px] font-semibold">Referral Partner Agreement</p>
                <span className="ml-auto text-[11px] text-white/40">v{REFERRAL_PARTNER_AGREEMENT_VERSION}</span>
              </div>
              <div className="max-h-[280px] overflow-y-auto px-4 py-4 space-y-4 text-[13px] leading-relaxed text-white/75">
                {REFERRAL_PARTNER_AGREEMENT_SECTIONS.map((section) => (
                  <div key={section.title}>
                    <p className="font-semibold text-white/90">{section.title}</p>
                    <p className="mt-1">{section.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/30 bg-transparent text-violet-500 focus:ring-violet-500"
              />
              <span className="text-[13px] leading-snug text-white/80">
                I have read and agree to the Referral Partner Agreement. I understand my referral
                link will only become active after I sign below.
              </span>
            </label>

            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-wider text-white/50 mb-2">
                Full legal name (electronic signature)
              </label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Type your full name"
                className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-white/35 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              />
            </div>

            {errorMessage ? (
              <p className="text-[13px] text-red-300 text-center">{errorMessage}</p>
            ) : null}

            <button
              type="button"
              disabled={!agreed || signerName.trim().length < 2 || pageState === 'claiming'}
              onClick={handleClaim}
              className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-violet-900/30 transition hover:from-violet-500 hover:to-blue-500 disabled:cursor-not-allowed disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {pageState === 'claiming' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Activating your link…
                </>
              ) : (
                'Accept & activate my referral link'
              )}
            </button>
          </div>
        ) : null}

        {(pageState === 'success' || pageState === 'already_claimed') && signupUrl ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-6 text-center backdrop-blur-sm">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20">
                <Check className="h-6 w-6 text-emerald-300" strokeWidth={2.5} />
              </div>
              <h2 className="text-[22px] font-bold">
                {pageState === 'success' ? 'You\'re all set!' : 'Already activated'}
              </h2>
              <p className="mt-2 text-[14px] text-white/70">
                {pageState === 'success'
                  ? 'Your referral partner link is now live. Share it with businesses you introduce to Nexus.'
                  : 'This partner invite has already been claimed. Here is your referral link.'}
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
                Your referral link
              </p>
              <p className="mt-2 break-all rounded-lg bg-black/30 px-3 py-2.5 font-mono text-[13px] text-violet-200">
                {signupUrl}
              </p>
              <button
                type="button"
                onClick={copyLink}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white py-3 text-[14px] font-semibold text-gray-900 transition hover:bg-white/90"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy referral link
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-[12px] text-white/45">
              Commission accrues on eligible platform subscriptions after referred businesses complete
              their trial.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
